import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    IconColorPicker,
    IconEraser,
    IconQrcode,
    IconSquare,
    IconSquareOff,
    IconX,
} from '@tabler/icons-react';
import { useLocation, Navigate } from 'react-router-dom';
import { extForBlob } from '../shared/imageOutputFormats';
import OutputFormatSelect from '../shared/OutputFormatSelect';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import { createSignatureDesktopRelay } from '../../../../../lib/utils/signaturePeerRelay';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import Hint from '../../../../../components/common/Hint';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import SignaturePadCanvas from './SignaturePadCanvas';
import SignatureMobileScanQrPanel from './SignatureMobileScanQrPanel';
import {
    BACKGROUND_MODES,
    PEN_COLORS,
    STROKE_WIDTHS,
    exportSignaturePng,
    outputFileName,
} from './signaturePadImageEngine';
import { generateRelaySlug, isValidRelaySlug } from './signaturePadRelayUtils';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

/** Glyph-over-label tile, the tray shape the other image tools use. */
function tileBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/** The nib itself, drawn at the width it will draw with. */
function StrokeGlyph({ width, active }) {
    const color = active ? '#F59E0B' : '#9A9AA3';
    return (
        <span className="flex h-[22px] w-[22px] items-center justify-center">
            <span style={{ background: color, width: 18, height: Math.max(2, width), borderRadius: 999 }} />
        </span>
    );
}

const BACKGROUND_ICONS = { transparent: IconSquareOff, white: IconSquare };

/** Ink that is not one of the presets means the picker tile is the live one. */
function isLightHex(hex) {
    const value = String(hex || '').replace('#', '');
    if (value.length !== 6) return true;
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
}

/** What the download comes out as: the lossless default first, then the lighter ones. */
const SIGNATURE_OUTPUT_FORMATS = ['png', 'webp', 'jpg', 'avif'];

export default function SignaturePadImage() {
    const location = useLocation();
    const search = new URLSearchParams(location.search);
    const legacySlug = search.get('s');
    if (search.get('m') === '1' && isValidRelaySlug(legacySlug)) {
        return <Navigate to={`/signature-mobile/${legacySlug}`} replace />;
    }
    return <SignaturePadDesktop />;
}

function SignaturePadDesktop() {
    const { t } = useTranslation();
    const padRef = useRef(null);
    const relayConsumedRef = useRef(false);
    const relaySlugRef = useRef(null);

    const [penColor, setPenColor] = useState(PEN_COLORS[0]);
    const [strokeWidth, setStrokeWidth] = useState(STROKE_WIDTHS[1]);
    const [backgroundMode, setBackgroundMode] = useState('transparent');
    const [format, setFormat] = useState('png');
    const [hasStroke, setHasStroke] = useState(false);
    const [signatureFromMobile, setSignatureFromMobile] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [error, setError] = useState(null);
    const [relaySessionSlug, setRelaySessionSlug] = useState(null);
    const [relayQrModalOpen, setRelayQrModalOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);
    const [qrSessionActive, setQrSessionActive] = useState(true);

    useEffect(() => {
        relaySlugRef.current = relaySessionSlug;
    }, [relaySessionSlug]);

    useEffect(() => {
        setRelaySessionSlug(generateRelaySlug());
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mq = window.matchMedia('(min-width: 768px) and (pointer: fine)');
        const update = () => setIsDesktop(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    const createNewQrSession = useCallback(() => {
        const oldSlug = relaySlugRef.current;
        if (typeof sessionStorage !== 'undefined' && oldSlug) {
            sessionStorage.setItem(`sig_session_used_${oldSlug}`, '1');
        }
        relayConsumedRef.current = false;
        setQrSessionActive(true);
        setRelaySessionSlug(generateRelaySlug());
        notifications.show({
            title: t('signaturePadImage.qr.newSessionTitle'),
            message: t('signaturePadImage.qr.newSessionMessage'),
            color: 'violet',
        });
    }, [t]);

    const handleRelayReceive = useCallback(async (files, stopRelay) => {
        if (relayConsumedRef.current) return;
        const file = files?.[0];
        if (!file || !padRef.current) return;

        const slug = relaySlugRef.current;
        relayConsumedRef.current = true;
        setQrSessionActive(false);
        stopRelay?.();

        if (typeof sessionStorage !== 'undefined' && slug) {
            sessionStorage.setItem(`sig_session_used_${slug}`, '1');
        }

        try {
            await padRef.current.loadFromFile(file);
            setSignatureFromMobile(true);
            setDownloaded(false);
            setError(null);
            notifications.show({
                title: t('common.success'),
                message: t('signaturePadImage.qr.received'),
                color: 'green',
            });
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('signaturePadImage.qr.loadFailed'),
                color: 'red',
            });
        } finally {
            setRelayQrModalOpen(false);
            setRelaySessionSlug(generateRelaySlug());
        }
    }, [t]);

    useEffect(() => {
        if (!relaySessionSlug) return undefined;
        relayConsumedRef.current = false;
        setQrSessionActive(true);
        const stop = createSignatureDesktopRelay(relaySessionSlug, (files) => {
            handleRelayReceive(files, stop);
        });
        return () => stop();
    }, [relaySessionSlug, handleRelayReceive]);

    const handleClear = () => {
        padRef.current?.clear();
        setSignatureFromMobile(false);
        setDownloaded(false);
        setError(null);
    };

    const handleDownload = async () => {
        if (!hasStroke || processing) return;

        const canvas = padRef.current?.getCanvas();
        if (!canvas) return;

        recordToolUsage('image/signature-pad', { category: 'image' });

        setProcessing(true);
        setError(null);

        try {
            const blob = await exportSignaturePng(canvas, { background: backgroundMode, format });
            downloadBlob(blob, outputFileName(extForBlob(blob)));
            setDownloaded(true);
            notifications.show({
                title: t('common.success'),
                message: t('signaturePadImage.done'),
                color: 'green',
            });
        } catch {
            const msg = t('signaturePadImage.errors.empty');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const handleReset = () => {
        handleClear();
    };

    const status = deriveToolStatus({
        hasInput: hasStroke,
        canRun: hasStroke,
        processing,
        hasResult: downloaded,
        hasError: !!error,
    });

    const isCustomPenColor = !PEN_COLORS.some(
        (color) => color.toLowerCase() === penColor.toLowerCase(),
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('signaturePadImage.statusReady'),
        success: t('signaturePadImage.done'),
    });

    return (
        <>
            {relayQrModalOpen ? (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#1C1815]/70 p-4 backdrop-blur-[3px]">
                    <div className="w-full max-w-md overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_20px_60px_rgba(28,24,21,0.3)] dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="flex items-center justify-between border-b border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                            <p className="text-[14px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                {t('signaturePadImage.qr.title')}
                            </p>
                            <button
                                type="button"
                                onClick={() => setRelayQrModalOpen(false)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] text-[#6B635C] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:text-zinc-300"
                                aria-label={t('common.close')}
                            >
                                <IconX size={16} stroke={2} />
                            </button>
                        </div>
                        <div className="p-4">
                            <SignatureMobileScanQrPanel
                                sessionSlug={relaySessionSlug}
                                inModal
                                onNewSession={createNewQrSession}
                                sessionReady={qrSessionActive}
                            />
                        </div>
                    </div>
                </div>
            ) : null}

            <ToolPageLayout
                slug="image/signature-pad"
                subtitleKey="signaturePadImage.subtitle"
                status={status}
                statusText={statusText}
                controlsTitle={t('signaturePadImage.padTitle')}
                showTrust={false}
                // The pad column tracks the settings column, so the two sides end
                // level instead of the pad running past the last setting.
                matchSidebarHeight
                onReset={hasStroke || downloaded || error ? handleReset : undefined}
                primaryAction={{
                    label: t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${format}`) }),
                    onClick: handleDownload,
                    disabled: !hasStroke || processing,
                    loading: processing,
                }}
                actionExtra={(
                    <OutputFormatSelect
                        formats={SIGNATURE_OUTPUT_FORMATS}
                        value={format}
                        onChange={setFormat}
                        disabled={processing}
                    />
                )}
                controls={(
                    <div className="space-y-3">
                        <ToolCard title={t('signaturePadImage.actionsTitle')}>
                            {/* Two actions, two columns — they are alternatives,
                                not steps, so they sit side by side. Icons only:
                                the name is in the tooltip and read out via
                                aria-label. */}
                            <div className={`grid gap-2 ${isDesktop ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                {isDesktop ? (
                                    <Hint label={t('signaturePadImage.qr.title')}>
                                        <button
                                            type="button"
                                            onClick={() => setRelayQrModalOpen(true)}
                                            aria-label={t('signaturePadImage.qr.title')}
                                            className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-1 py-1.5 text-[11px] font-semibold leading-tight text-violet-700 transition-colors hover:border-violet-300 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-300"
                                        >
                                            <IconQrcode size={20} stroke={1.8} />
                                        </button>
                                    </Hint>
                                ) : null}
                                <Hint label={t('signaturePadImage.clear')}>
                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        disabled={!hasStroke}
                                        aria-label={t('signaturePadImage.clear')}
                                        className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border border-[#ECE8E4] bg-white px-1 py-1.5 text-[11px] font-semibold leading-tight text-[#3D3833] transition-colors hover:border-amber-400/40 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                    >
                                        <IconEraser size={20} stroke={1.8} />
                                    </button>
                                </Hint>
                            </div>
                        </ToolCard>

                        {signatureFromMobile ? (
                            <p className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] font-medium text-violet-700 dark:border-violet-800 dark:bg-violet-900/20 dark:text-violet-300">
                                {t('signaturePadImage.qr.fromPhone')}
                            </p>
                        ) : null}
                    </div>
                )}
                settings={(
                    <div className="space-y-3.5">
                        <ToolCard title={t('signaturePadImage.penTitle')}>
                            {/* Presets and the free picker on one row — the last
                                tile is the picker, tinted with the ink it holds. */}
                            <div className="flex flex-wrap items-center gap-2">
                                {PEN_COLORS.map((color) => (
                                    <button
                                        key={color}
                                        type="button"
                                        onClick={() => setPenColor(color)}
                                        aria-label={color}
                                        className={`h-9 w-9 rounded-lg border-2 transition-transform hover:scale-105 ${
                                            penColor.toLowerCase() === color.toLowerCase()
                                                ? 'border-amber-500 ring-2 ring-amber-400/40'
                                                : 'border-[#ECE8E4] dark:border-zinc-700'
                                        }`}
                                        style={{ backgroundColor: color }}
                                    />
                                ))}
                                <Hint label={t('signaturePadImage.customColor')}>
                                <label
                                    aria-label={t('signaturePadImage.customColor')}
                                    className={`relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-2 transition-transform ${
                                        isCustomPenColor
                                            ? 'border-amber-500 ring-2 ring-amber-400/40'
                                            : 'border-[#ECE8E4] dark:border-zinc-700'
                                    }`}
                                    style={isCustomPenColor ? { backgroundColor: penColor } : undefined}
                                >
                                    <IconColorPicker
                                        size={16}
                                        stroke={2}
                                        className={isCustomPenColor && !isLightHex(penColor)
                                            ? 'text-white'
                                            : 'text-[#6B635C] dark:text-zinc-300'}
                                    />
                                    <input
                                        type="color"
                                        value={penColor}
                                        onChange={(e) => setPenColor(e.target.value)}
                                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                    />
                                </label>
                                </Hint>
                            </div>

                            <div className="mt-3 space-y-1.5">
                                <span className="block text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('signaturePadImage.strokeTitle')}
                                </span>
                                <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                    {/* Glyphs only — each line is drawn at its own
                                        weight, and the name is in the tooltip /
                                        aria-label. */}
                                    {STROKE_WIDTHS.map((width) => (
                                        <Hint label={t(`signaturePadImage.strokes.${width}`)} key={width}>
                                            <button
                                                type="button"
                                                onClick={() => setStrokeWidth(width)}
                                                aria-label={t(`signaturePadImage.strokes.${width}`)}
                                                aria-pressed={strokeWidth === width}
                                                className={tileBtnClass(strokeWidth === width)}
                                            >
                                                <StrokeGlyph width={width} active={strokeWidth === width} />
                                            </button>
                                        </Hint>
                                    ))}
                                </div>
                            </div>
                        </ToolCard>

                        <ToolCard title={t('signaturePadImage.backgroundTitle')}>
                            <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {/* Icons only — the name is in the tooltip and read
                                    out via aria-label. */}
                                {BACKGROUND_MODES.map((mode) => {
                                    const Icon = BACKGROUND_ICONS[mode] || IconSquare;
                                    const label = t(`signaturePadImage.backgrounds.${mode}`);
                                    return (
                                        <Hint label={label} key={mode}>
                                            <button
                                                type="button"
                                                onClick={() => setBackgroundMode(mode)}
                                                aria-label={label}
                                                aria-pressed={backgroundMode === mode}
                                                className={tileBtnClass(backgroundMode === mode)}
                                            >
                                                <Icon size={20} stroke={1.8} />
                                            </button>
                                        </Hint>
                                    );
                                })}
                            </div>
                            <p className="mt-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                {t('signaturePadImage.backgroundHint')}
                            </p>
                        </ToolCard>
                    </div>
                )}
                preview={(
                    <ToolPreviewPanel
                        title={t('signaturePadImage.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('signaturePadImage.previewEmpty')}
                        idleHint={t('signaturePadImage.previewEmptyHint')}
                        processingLabel={t('signaturePadImage.exporting')}
                        error={error}
                        showIdle={false}
                        fill
                        tall={false}
                        readyHint={status === 'ready' ? (
                            <ToolReadyHint actionLabel={t('signaturePadImage.download')} />
                        ) : null}
                    >
                        <SignaturePadCanvas
                            ref={padRef}
                            penColor={penColor}
                            strokeWidth={strokeWidth}
                            backgroundMode={backgroundMode}
                            emptyHint={t('signaturePadImage.drawHint')}
                            onStrokeChange={(next) => {
                                setHasStroke(next);
                                if (next) {
                                    setSignatureFromMobile(false);
                                    setDownloaded(false);
                                    setError(null);
                                }
                            }}
                        />
                    </ToolPreviewPanel>
                )}
            />
        </>
    );
}
